import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { EmptyState, Field, Select, Spinner } from '@zt/ui';
import { Figure, Section, Table, td, tdR, th, thR } from '../components/reports/blocks';
import { PeriodControls, usePeriod } from '../components/reports/period';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import type { MultiTouchReport } from '../lib/types-faz8';

/**
 * Çok dokunuşlu atribüsyon (F8-14, ADR-814): kesinleşen vitrin siparişleri ilk temas, son temas, doğrusal ve konuma
 * göre (U) modelle kaynağa dağıtılır; dört model yan yana. Rehber: docs/faz-8/REPORTS_FINANCE_TR.md#coklu-dokunus.
 */
const MODELS = ['first', 'last', 'linear', 'position'] as const;

export function MultiTouchPage() {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const period = usePeriod('last_30');
  const { stores } = useStoreChoice();
  const [storeId, setStoreId] = useState('');
  const [dimension, setDimension] = useState<'source_medium' | 'campaign'>('source_medium');
  const params = new URLSearchParams({ from: period.from, to: period.to, dimension });
  if (storeId) params.set('storeId', storeId);
  const q = useQuery({
    queryKey: ['multi-touch', params.toString()],
    queryFn: () => api.get<MultiTouchReport>(`/v1/admin/reports/multi-touch?${params}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const orders = (bps: number) => formatNumber(bps / 10_000, 2);
  const label = (r: MultiTouchReport['rows'][number]) =>
    r.kind === 'touch'
      ? (r.label ?? t('admin.multiTouch.noCampaign'))
      : t(`admin.multiTouch.kinds.${r.kind}`);
  return (
    <PageFrame helpKey="page.multi_touch" wide>
      <div className="flex flex-wrap items-end gap-4">
        <PeriodControls period={period} showInterval={false} />
        {stores.length > 1 && (
          <Field label={t('admin.salesReport.store')} help={<HelpTip k="sales_report.store" />}>
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">{t('admin.reports.all')}</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.multiTouch.dimension')}>
          {(p) => (
            <Select
              {...p}
              value={dimension}
              onChange={(e) => setDimension(e.target.value as 'source_medium' | 'campaign')}
            >
              <option value="source_medium">{t('admin.multiTouch.dimensions.source_medium')}</option>
              <option value="campaign">{t('admin.multiTouch.dimensions.campaign')}</option>
            </Select>
          )}
        </Field>
      </div>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div
          className={
            q.isFetching ? 'space-y-6 opacity-60 transition-opacity' : 'space-y-6 transition-opacity'
          }
        >
          <section
            aria-label={t('admin.multiTouch.summary')}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            <Figure
              label={t('admin.multiTouch.orders')}
              value={formatNumber(d.orders, 0)}
              sub={money(d.revenue, 'TRY')}
            />
            <Figure
              label={t('admin.multiTouch.attributed')}
              value={formatNumber(d.attributedOrders, 0)}
              sub={t('admin.multiTouch.attributedOf')}
            />
            <Figure
              label={t('admin.multiTouch.withPath')}
              value={formatNumber(d.ordersWithPath, 0)}
              sub={t('admin.multiTouch.withPathOf')}
            />
            <Figure
              label={t('admin.multiTouch.avgTouches')}
              value={d.avgTouchesX100 === null ? '—' : formatNumber(d.avgTouchesX100 / 100, 2)}
            />
          </section>
          <Section title={t('admin.multiTouch.tableTitle')} help={<HelpTip k="multi_touch.models" />}>
            {!d.rows.length ? (
              <EmptyState title={t('admin.multiTouch.empty')} />
            ) : (
              <Table
                caption={t('admin.multiTouch.tableTitle')}
                head={
                  <>
                    <tr>
                      <th scope="col" rowSpan={2} className={th}>
                        {t(`admin.multiTouch.dimensions.${d.dimension}`)}
                      </th>
                      {MODELS.map((m) => (
                        <th key={m} scope="colgroup" colSpan={2} className={`${th} text-center`}>
                          {t(`admin.multiTouch.models.${m}`)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {MODELS.flatMap((m) => [
                        <th key={`${m}-o`} scope="col" className={thR}>
                          <span className="sr-only">{t(`admin.multiTouch.models.${m}`)}: </span>
                          {t('admin.multiTouch.orderShare')}
                        </th>,
                        <th key={`${m}-r`} scope="col" className={thR}>
                          <span className="sr-only">{t(`admin.multiTouch.models.${m}`)}: </span>
                          {t('admin.multiTouch.revenue')}
                        </th>,
                      ])}
                    </tr>
                  </>
                }
              >
                {d.rows.map((r) => (
                  <tr key={r.key} className="border-t border-line">
                    <th scope="row" className={`${td} text-start font-normal`}>
                      {label(r)}
                    </th>
                    {MODELS.flatMap((m) => [
                      <td key={`${m}-o`} className={tdR}>
                        {orders(r[m].ordersBps)}
                      </td>,
                      <td key={`${m}-r`} className={tdR}>
                        {money(r[m].revenue, 'TRY')}
                      </td>,
                    ])}
                  </tr>
                ))}
              </Table>
            )}
            <div className="mt-2 space-y-1 text-xs text-ink-3">
              <p>{t('admin.multiTouch.note')}</p>
              <p>{t('admin.multiTouch.pathNote')}</p>
              {d.fxMissing > 0 && (
                <p className="text-amber">
                  {t('admin.multiTouch.fxMissing', { count: formatNumber(d.fxMissing, 0) })}
                </p>
              )}
            </div>
          </Section>
          <p className="text-sm">
            <Link to="/kaynaklar" className="text-pine hover:underline">
              {t('admin.marketingSummary.openAttribution')}
            </Link>
          </p>
        </div>
      )}
    </PageFrame>
  );
}
